import { useEffect, useRef } from "react";
import { TrendingUp, TrendingDown, BarChart2, Users, ShoppingBag } from "lucide-react";

// ─── Multi-Ring Donut Canvas ──────────────────────────────────────────────────
function MultiRingDonut({ segments = [], size = 140 }) {
  const ref = useRef(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    canvas.width = size;
    canvas.height = size;
    const cx = size / 2;
    const cy = size / 2;
    const rw = 11;
    const gap = 9;

    ctx.clearRect(0, 0, size, size);

    segments.forEach((seg, i) => {
      const r = size / 2 - i * (rw + gap) - 8;

      // track
      ctx.beginPath();
      ctx.arc(cx, cy, r, 0, 2 * Math.PI);
      ctx.strokeStyle = "rgba(255,255,255,0.15)";
      ctx.lineWidth = rw;
      ctx.stroke();

      // value arc
      const start = -Math.PI / 2;
      const end = start + (seg.pct / 100) * 2 * Math.PI;
      ctx.beginPath();
      ctx.arc(cx, cy, r, start, end);
      ctx.strokeStyle = seg.color;
      ctx.lineWidth = rw;
      ctx.lineCap = "round";
      ctx.stroke();
    });
  }, [segments, size]);

  return <canvas ref={ref} style={{ display: "block" }} />;
}

// ─── Badge ────────────────────────────────────────────────────────────────────
function ChangeBadge({ value }) {
  const num = parseFloat(value);
  const isNeg = num < 0;

  return (
    <span
      style={{
        fontSize: 11,
        fontWeight: 600,
        padding: "3px 9px",
        borderRadius: 20,
        display: "inline-flex",
        alignItems: "center",
        gap: 3,
        background: isNeg ? "rgba(239,68,68,0.25)" : "rgba(52,211,153,0.2)",
        color: isNeg ? "#FCA5A5" : "#6EE7B7",
        whiteSpace: "nowrap",
      }}
    >
      {isNeg ? <TrendingDown size={10} /> : <TrendingUp size={10} />}
      {value}
    </span>
  );
}

// ─── Metric Row ───────────────────────────────────────────────────────────────
function MetricRow({ icon: Icon, iconColor, iconBg, label, value, change }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "10px 12px",
        borderRadius: 12,
        border: "1px solid rgba(255,255,255,0.15)",
        background: "rgba(255,255,255,0.1)",
        backdropFilter: "blur(8px)",
        WebkitBackdropFilter: "blur(8px)",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <div
          style={{
            width: 34,
            height: 34,
            borderRadius: 9,
            background: iconBg,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <Icon size={15} color={iconColor} />
        </div>
        <div>
          <p
            style={{
              fontSize: 11,
              color: "rgba(255,255,255,0.55)",
              margin: 0,
              lineHeight: 1.2,
              fontWeight: 400,
            }}
          >
            {label}
          </p>
          <p
            style={{
              fontSize: label === "Top product" ? 13 : 17,
              fontWeight: 700,
              margin: "2px 0 0",
              lineHeight: 1.2,
              color: "#fff",
              maxWidth: 150,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {value}
          </p>
        </div>
      </div>
      <ChangeBadge value={change} />
    </div>
  );
}

// ─── Legend Item ──────────────────────────────────────────────────────────────
function LegendItem({ color, label }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
      <div
        style={{
          width: 8,
          height: 8,
          borderRadius: "50%",
          background: color,
          flexShrink: 0,
          boxShadow: `0 0 5px ${color}88`,
        }}
      />
      <span style={{ fontSize: 11, color: "rgba(255,255,255,0.55)", lineHeight: 1 }}>
        {label}
      </span>
    </div>
  );
}

// ─── Main Card ────────────────────────────────────────────────────────────────
export default function DashboardOverviewCard({
  totalOnboarding = 0,
  totalProductSales = 0,
  customerCount = 0,
  onboardingChange = "0%",
  customerChange = "0%",
  productChange = "0%",
  topProductName = "-",
}) {
  const total =
    (totalOnboarding || 0) + (totalProductSales || 0) + (customerCount || 0);

  const pctOf = (v) => {
    if (!total || isNaN(total)) return 0;
    return Math.round((v / total) * 100);
  };

  const fmtCount = (n) => {
    n = n || 0;
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
    if (n >= 1_000)
      return `${Math.floor(n / 1000)},${String(n % 1000).padStart(3, "0")}`;
    return String(n);
  };

  const donutSegments = [
    { pct: pctOf(totalOnboarding),   color: "#22D3EE" },
    { pct: pctOf(customerCount),     color: "#A78BFA" },
    { pct: pctOf(totalProductSales), color: "#34D399" },
  ];

  const activePct = total
    ? Math.round(((totalOnboarding + customerCount) / total) * 100)
    : 0;

  return (
    <div
      style={{
        position: "relative",
        borderRadius: 20,
        padding: "22px 22px 20px",
        display: "flex",
        flexDirection: "column",
        height: "100%",
        boxSizing: "border-box",
        overflow: "hidden",
        fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
        background:
          "linear-gradient(135deg, #1e3a5f 0%, #2563eb 55%, #7c3aed 100%)",
      }}
    >
      {/* Decorative blobs */}
      <div
        style={{
          position: "absolute", top: -40, right: -40,
          width: 160, height: 160, borderRadius: "50%",
          background: "rgba(255,255,255,0.07)", pointerEvents: "none",
        }}
      />
      <div
        style={{
          position: "absolute", bottom: -30, left: -30,
          width: 120, height: 120, borderRadius: "50%",
          background: "rgba(255,255,255,0.06)", pointerEvents: "none",
        }}
      />
      <div
        style={{
          position: "absolute", top: 80, right: 60,
          width: 60, height: 60, borderRadius: "50%",
          background: "rgba(255,255,255,0.05)", pointerEvents: "none",
        }}
      />

      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          marginBottom: 18,
          position: "relative",
          zIndex: 1,
        }}
      >
        <div>
          <p
            style={{
              fontSize: 10, fontWeight: 600,
              letterSpacing: "0.1em", textTransform: "uppercase",
              color: "rgba(255,255,255,0.45)", margin: "0 0 4px",
            }}
          >
            Overview
          </p>
          <p
            style={{
              fontSize: 17, fontWeight: 700,
              color: "#fff", margin: 0, lineHeight: 1.2,
            }}
          >
            Dashboard summary
          </p>
        </div>
        <span
          style={{
            fontSize: 11, padding: "4px 10px", borderRadius: 20,
            background: "rgba(255,255,255,0.12)",
            color: "rgba(255,255,255,0.7)",
            border: "1px solid rgba(255,255,255,0.2)",
            fontWeight: 500,
          }}
        >
          This period
        </span>
      </div>

      {/* Body */}
      <div
        style={{
          display: "flex", gap: 18, alignItems: "flex-start",
          flex: 1, position: "relative", zIndex: 1,
        }}
      >
        {/* Left: Donut + Legend */}
        <div
          style={{
            width:"60%", display: "flex", flexDirection: "column",
            alignItems: "center", gap: 14, flexShrink: 0,
          }}
        >
          <div style={{ position: "relative", display: "inline-flex" }}>
            <MultiRingDonut segments={donutSegments} size={200} />
            <div
              style={{
                position: "absolute", inset: 0,
                display: "flex", flexDirection: "column",
                alignItems: "center", justifyContent: "center",
                pointerEvents: "none",
              }}
            >
              <span style={{ fontSize: 20, fontWeight: 800, color: "#fff", lineHeight: 1 }}>
                {activePct}%
              </span>
              <span style={{ fontSize: 10, color: "rgba(255,255,255,0.5)", marginTop: 3 }}>
                active
              </span>
            </div>
          </div>

          <div style={{ display: "flex", flexDirection: "row", gap: 6 }}>
            <LegendItem color="#22D3EE" label="Retailer onboarding" />
            <LegendItem color="#A78BFA" label="Customer growth" />
            <LegendItem color="#34D399" label="Warranty sales" />
          </div>
        </div>

        {/* Right: Metric rows */}
        <div style={{width:"40%", flex: 1, display: "flex", flexDirection: "column", gap: 8 }}>
          <MetricRow
            icon={BarChart2}
            iconBg="rgba(34,211,238,0.2)"
            iconColor="#22D3EE"
            label="Total onboarded"
            value={fmtCount(totalOnboarding)}
            change={onboardingChange}
          />
          <MetricRow
            icon={Users}
            iconBg="rgba(167,139,250,0.2)"
            iconColor="#A78BFA"
            label="Warranties sold"
            value={fmtCount(totalProductSales)}
            change={customerChange}
          />
          <MetricRow
            icon={ShoppingBag}
            iconBg="rgba(52,211,153,0.2)"
            iconColor="#34D399"
            label="Top product"
            value={topProductName || "-"}
            change={productChange}
          />
        </div>
      </div>
    </div>
  );
}


// import { useEffect, useRef } from "react";
// import { TrendingUp, Users, ShoppingBag, BarChart2 } from "lucide-react";

// // ─── Mini Donut Chart ─────────────────────────────────────────────────────────
// function MultiRingDonut({ data, size = 220 }) {
// const ref = useRef(null);

// useEffect(() => {
//     const canvas = ref.current;
//     if (!canvas) return;

//     const ctx = canvas.getContext("2d");
//     canvas.width = size;
//     canvas.height = size;

//     const cx = size / 2;
//     const cy = size / 2;

//     ctx.clearRect(0, 0, size, size);

//     const ringWidth = 12;
//     const gap = 8;

//     data.forEach((item, index) => {
//     const radius = size / 2 - index * (ringWidth + gap) - 10;

//     // background circle (light gray)
//     ctx.beginPath();
//     ctx.arc(cx, cy, radius, 0, 2 * Math.PI);
//     ctx.strokeStyle = "rgba(255,255,255,0.15)";
//     ctx.lineWidth = ringWidth;
//     ctx.stroke();

//     // value arc
//     const start = -Math.PI / 2;
//     const end = start + (item.value / 100) * 2 * Math.PI;

//     ctx.beginPath();
//     ctx.arc(cx, cy, radius, start, end);
//     ctx.strokeStyle = item.color;
//     ctx.lineWidth = ringWidth;
//     ctx.lineCap = "round";
//     ctx.stroke();
//     });

// }, [data, size]);

// return <canvas ref={ref} />;
// }

// // ─── Stat Row ─────────────────────────────────────────────────────────────────
// function StatRow({ icon: Icon, iconColor, iconBg, label, value, change }) {
// return (
//     <div
//     style={{
//         display: "flex",
//         alignItems: "center",
//         justifyContent: "space-between",
//         padding: "10px 14px",
//         borderRadius: "10px",
//         background: "rgba(255,255,255,0.08)",
//         border: "1px solid rgba(255,255,255,0.12)",
//         backdropFilter: "blur(6px)",
//     }}
//     >
//     <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
//         <div
//         style={{
//             width: 32,
//             height: 32,
//             borderRadius: 9,
//             background: iconBg,
//             display: "flex",
//             alignItems: "center",
//             justifyContent: "center",
//             flexShrink: 0,
//         }}
//         >
//         <Icon size={14} color={iconColor} />
//         </div>
//         <div>
//         <p style={{ fontSize: 10, color: "rgba(255,255,255,0.5)", margin: 0, lineHeight: 1 }}>
//             {label}
//         </p>
//         <p style={{ fontSize: 15, fontWeight: 700, color: "#fff", margin: "2px 0 0", lineHeight: 1 }}>
//             {value}
//         </p>
//         </div>
//     </div>
//     <span
//         style={{
//         fontSize: 10,
//         fontWeight: 600,
//         color: "#6ee7b7",
//         background: "rgba(110,231,183,0.15)",
//         padding: "3px 8px",
//         borderRadius: 20,
//         display: "flex",
//         alignItems: "center",
//         gap: 3,
//         }}
//     >
//         <TrendingUp size={9} color="#6ee7b7" />
//         {change}
//     </span>
//     </div>
// );
// }

// // ─── Main Card ────────────────────────────────────────────────────────────────
// export default function DashboardOverviewCard({
// totalOnboarding,
// totalProductSales,
// customerCount,
// onboardingChange,
// customerChange,
// productChange,
// topProductName,
// }) {
// const total = totalOnboarding + totalProductSales + customerCount;
// const pctOf = (v) => {
//   if (!total || isNaN(total)) return 0;
//   return Math.round((v / total) * 100);
// };

// const segments = [
//     { value: totalOnboarding,   color: "#a5f3fc" },
//     { value: customerCount,     color: "#c4b5fd" },
//     { value: totalProductSales, color: "#6ee7b7" },
// ];

// const legendItems = [
//     { color: "#a5f3fc", label: "Retailer onboarding", pct: pctOf(totalOnboarding) },
//     { color: "#c4b5fd", label: "Customer growth",     pct: pctOf(customerCount) },
//     { color: "#6ee7b7", label: "Product sales",       pct: pctOf(totalProductSales) },
// ];

// const fmtCount = (n) =>
//     n >= 1_000
//     ? `${Math.floor(n / 1000)},${String(n % 1000).padStart(3, "0")}`
//     : String(n);

// return (
// <div className="relative flex flex-col h-full py-2 px-4 rounded-2xl overflow-hidden text-white
//     bg-gradient-to-br from-blue-900 via-blue-600 to-purple-600 font-sans">

//     {/* Decorative blobs */}
//     <div className="absolute -top-12 -right-12 w-44 h-44 rounded-full bg-white/10" />
//     <div className="absolute -bottom-8 -left-8 w-28 h-28 rounded-full bg-white/10" />
//     <div className="absolute top-24 right-8 w-14 h-14 rounded-full bg-white/10" />

//     {/* Header */}
//     <p className="text-[10px] font-semibold tracking-widest text-white/60 uppercase">
//     Overview
//     </p>
//     <h3 className="text-lg font-bold mb-4">Dashboard summary</h3>

//     {/* Content */}
//     <div className="flex items-center gap-4 mb-4">

//     {/* Donut */}
//     <div className="relative">
//         <MultiRingDonut
//             size={240}
//             data={[
//                 { value: pctOf(totalOnboarding), color: "#22d3ee" }, // cyan
//                 { value: pctOf(customerCount), color: "#a78bfa" },   // purple
//                 { value: pctOf(totalProductSales), color: "#34d399" } // green
//             ]}
//             />
//         <div className="absolute inset-0 flex flex-col items-center justify-center">
//         <span className="text-lg font-extrabold">
//             {total ? pctOf(totalOnboarding + customerCount) : 0}%
//         </span>
//         <span className="text-[10px] text-white/60">active</span>
//         </div>
//     </div>

//     {/* Stats */}
//     <div className="flex flex-col gap-2 w-full">

//         {/* Row */}
//         <div className="flex items-center justify-between p-2 rounded-lg bg-white/10 border border-white/20 backdrop-blur">
//         <div className="flex items-center gap-2">
//             <div className="w-8 h-8 flex items-center justify-center rounded-md bg-cyan-200/20">
//             <BarChart2 size={14} className="text-cyan-200" />
//             </div>
//             <div>
//             <p className="text-[10px] text-white/60">Total onboarded</p>
//             <p className="text-sm font-bold">{fmtCount(totalOnboarding)}</p>
//             </div>
//         </div>
//         <span className="text-[10px] px-2 py-1 rounded-full bg-green-200/20 text-green-300 flex items-center gap-1">
//             <TrendingUp size={10} />
//             {onboardingChange}
//         </span>
//         </div>

//         <div className="flex items-center justify-between p-2 rounded-lg bg-white/10 border border-white/20 backdrop-blur">
//         <div className="flex items-center gap-2">
//             <div className="w-8 h-8 flex items-center justify-center rounded-md bg-cyan-200/20">
//             <Users size={14} className="text-cyan-200" />
//             </div>
//             <div>
//             <p className="text-[10px] text-white/60">Warranty Sold</p>
//             <p className="text-sm font-bold">{fmtCount(totalProductSales)}</p>
//             </div>
//         </div>
//         <span className="text-[10px] px-2 py-1 rounded-full bg-green-200/20 text-green-300 flex items-center gap-1">
//             <TrendingUp size={10} />
//             {customerChange}
//         </span>
//         </div>

//         <div className="flex items-center justify-between p-2 rounded-lg bg-white/10 border border-white/20 backdrop-blur">
//         <div className="flex items-center gap-2">
//             <div className="w-8 h-8 flex items-center justify-center rounded-md bg-cyan-200/20">
//             <ShoppingBag size={14} className="text-cyan-200" />
//             </div>
//             <div>
//             <p className="text-[10px] text-white/60">Top saled Product</p>
//             {/* <p className="text-sm font-bold truncate max-w-[140px]">
//                 {topProductName}
//                 </p> */}
//             <p className="text-sm font-bold truncate max-w-[140px]">
//             {topProductName}
//             </p>
//             </div>
//         </div>
//         <span className="text-[10px] px-2 py-1 rounded-full bg-green-200/20 text-green-300 flex items-center gap-1">
//             <TrendingUp size={10} />
//             {productChange}
//         </span>
//         </div>
//     </div>
//     </div>
// </div>
// );
// }
